import { IPOSConfig, PaymentMethodTypes } from "@/types/db/pos-config";
import i18n from "@/lang";
// Simplified type alias to avoid TS2589 (excessively deep type instantiation)
// caused by vue-i18n v11's complex overloaded generics on t()
const t: (key: string, ...args: unknown[]) => string = (i18n.global as any).t;
import POSConfigModule from "@/store/modules/pos-config";
import { fetchAllPCsTimeslots } from "../helpers/timeslots";
import { POSConfigErrors } from "@/constants/errors/pos-config";
import {
  fetchAllTicketGroupsPCs,
  checkAndFetchPCAddons,
} from "../helpers/product-category";
import { PCTypes } from "@/types/db/product-category";
import { LogLevel, LogService } from "@/services/logging";
import { computed } from "vue";
import { PaymentMethod } from "@smeetz/orders";
import { getSmeetzPictures } from "@/api/gql/common";
import {
  createCroppedImageproxyUrl,
  SmeetzPictureEntityTables,
  SmeetzPictureEntityTypes,
} from "../helpers/pictures";
import CommonModule from "@/store/modules/common";
import { dexieDB } from "@/services/dexie-database";
import { clearUnfinishedOrders } from "./orders.helper";

/**
 * Maps the legacy smeetz-pos PaymentMethodTypes to the new @smeetz/orders PaymentMethod enum.
 *
 * @param legacyPaymentMethod - The legacy payment method type.
 * @returns The corresponding new PaymentMethod enum value.
 */
export function paymentMethodToOrderPaymentMethod(
  legacyPaymentMethod: PaymentMethodTypes
): PaymentMethod {
  switch (legacyPaymentMethod) {
    case PaymentMethodTypes.Cash:
      return PaymentMethod.CASH;
    case PaymentMethodTypes.CardAuto:
      return PaymentMethod.POS_ADYEN;
    case PaymentMethodTypes.CardManual:
      return PaymentMethod.POS_CARD;
    case PaymentMethodTypes.Invoice:
      return PaymentMethod.INVOICE;
    case PaymentMethodTypes.ToBeInvoiced:
      return PaymentMethod.TO_BE_INVOICED;
    case PaymentMethodTypes.BankTransfer:
      return PaymentMethod.BANK_TRANSFER;
    case PaymentMethodTypes.PosReka:
      return PaymentMethod.POS_REKA;
    case PaymentMethodTypes.DiverseVoucher:
      return PaymentMethod.POS_DIVERSE_VOUCHER;
    case PaymentMethodTypes.POS_ANCV_CONNECT:
      return PaymentMethod.POS_ANCV_CONNECT;
    case PaymentMethodTypes.EXTERNAL_INCERT_VOUCHER:
      return PaymentMethod.EXTERNAL_INCERT_VOUCHER;
    case PaymentMethodTypes.Swiss_QR_Code_Bank_Transfer:
      return PaymentMethod.Swiss_QR_Code_Bank_Transfer;
    case PaymentMethodTypes.TOUT_UN_CANTON:
      return PaymentMethod.TOUT_UN_CANTON;
    case PaymentMethodTypes.CHAPLINS:
      return PaymentMethod.CHAPLINS;
    case PaymentMethodTypes.Cheque:
      return PaymentMethod.CHEQUE;
    case PaymentMethodTypes.ChequeVacances:
      return PaymentMethod.CHEQUE_VACANCES;
    case PaymentMethodTypes.chequeCulture:
      return PaymentMethod.CHEQUE_CULTURE;
    case PaymentMethodTypes.passCulture:
      return PaymentMethod.PASS_CULTURE;
    case PaymentMethodTypes.chorus:
      return PaymentMethod.CHORUS;
    case PaymentMethodTypes.CE:
      return PaymentMethod.CE;
    case PaymentMethodTypes.OCCYG:
      return PaymentMethod.OCCYG;
    case PaymentMethodTypes.CouponSport:
      return PaymentMethod.COUPON_SPORT;
    default:
      return PaymentMethod.UNKNOWN;
  }
}

export const PaymentMethodToTranslatedString = computed<{
  [key in PaymentMethod]: string;
}>(() => {
  const groupId = POSConfigModule.groupId;

  return {
    [PaymentMethod.CASH]: t("button.cash"),
    [PaymentMethod.POS_ADYEN]: t("button.card"),
    [PaymentMethod.POS_CARD]: `${t("button.card")} ${t(
      "button.manual-processing"
    )}`,
    [PaymentMethod.INVOICE]: t("button.invoice"),
    [PaymentMethod.TO_BE_INVOICED]: t("payment-method.TO_BE_INVOICED"),
    [PaymentMethod.BANK_TRANSFER]:
      t("button.bank-transfer") +
      (groupId &&
      [1, 16342 /*DEV*/, 16383 /*TEST*/, 19329 /*Hermitage*/].includes(groupId)
        ? "\n/ Bon cadeau (ancien)"
        : ""),
    [PaymentMethod.POS_REKA]: t("payment-method.POS_REKA"),
    [PaymentMethod.POS_DIVERSE_VOUCHER]: t(
      "payment-method.POS_DIVERSE_VOUCHER"
    ),
    [PaymentMethod.POS_ANCV_CONNECT]: t("payment-method.POS_ANCV_CONNECT"),
    [PaymentMethod.EXTERNAL_INCERT_VOUCHER]: t("payment-method.Incert_Voucher"),
    [PaymentMethod.Swiss_QR_Code_Bank_Transfer]: t(
      "payment-method.swiss-qr-code-bank-transfer"
    ),
    [PaymentMethod.TOUT_UN_CANTON]: t("payment-method.TOUT_UN_CANTON"),
    [PaymentMethod.CHAPLINS]: t("payment-method.CHAPLINS"),
    [PaymentMethod.CHEQUE]: t("button.cheque"),
    [PaymentMethod.CHEQUE_VACANCES]: t("button.cheque-vacances"),
    [PaymentMethod.CHEQUE_CULTURE]: t("button.cheque-culture"),
    [PaymentMethod.PASS_CULTURE]: t("button.pass-culture"),
    [PaymentMethod.CHORUS]: t("button.chorus"),
    [PaymentMethod.CE]: t("button.CE"),
    [PaymentMethod.OCCYG]: t("button.OCCYG"),
    [PaymentMethod.COUPON_SPORT]: t("button.coupon-sport"),
  } as { [key in PaymentMethod]: string };
});

export const PaymentMethodTypeToTranslatedString = computed<{
  [key in PaymentMethodTypes]: string;
}>(() => {
  // Object that converts PaymentMethodType to translated string
  const groupId = POSConfigModule.groupId;
  return {
    [PaymentMethodTypes.Cash]: t("button.cash"),
    [PaymentMethodTypes.CardAuto]: t("button.card"),
    [PaymentMethodTypes.CardManual]: `${t("button.card")} ${t(
      "button.manual-processing"
    )}`,
    [PaymentMethodTypes.Invoice]: t("button.invoice"),
    [PaymentMethodTypes.ToBeInvoiced]: t("payment-method.TO_BE_INVOICED"),
    // For Hermitage (group 19329) we need to show "Virement bancaire/Bon cadeau (ancien)" instead of just "Virement bancaire"
    [PaymentMethodTypes.BankTransfer]:
      t("button.bank-transfer") +
      (groupId &&
      [1, 16342 /*DEV*/, 16383 /*TEST*/, 19329 /*Hermitage*/].includes(groupId)
        ? "\n/ Bon cadeau (ancien)"
        : ""),
    // [PaymentMethodTypes.GiftCard]: t("button.gift-card"),
    [PaymentMethodTypes.Cheque]: t("button.cheque"),
    [PaymentMethodTypes.ChequeVacances]: t("button.cheque-vacances"),
    [PaymentMethodTypes.chequeCulture]: t("button.cheque-culture"),
    [PaymentMethodTypes.passCulture]: t("button.pass-culture"),
    [PaymentMethodTypes.chorus]: t("button.chorus"),
    [PaymentMethodTypes.CE]: t("button.CE"),
    [PaymentMethodTypes.OCCYG]: t("button.OCCYG"),
    [PaymentMethodTypes.CouponSport]: t("button.coupon-sport"),
    [PaymentMethodTypes.PosReka]: t("payment-method.POS_REKA"),
    [PaymentMethodTypes.DiverseVoucher]: t(
      "payment-method.POS_DIVERSE_VOUCHER"
    ),

    [PaymentMethodTypes.CFF]: t("payment-method.cff"),
    [PaymentMethodTypes.CGN]: t("payment-method.cgn"),
    [PaymentMethodTypes.QOQA]: t("payment-method.qoqa"),
    [PaymentMethodTypes.DEINDEAL]: t("payment-method.deindeal"),
    [PaymentMethodTypes.REALDEAL]: t("payment-method.realdeal"),
    [PaymentMethodTypes.EXTERNAL_INCERT_VOUCHER]: t(
      "payment-method.Incert_Voucher"
    ),
    [PaymentMethodTypes.Swiss_QR_Code_Bank_Transfer]: t(
      "payment-method.BANK_TRANSFER_SWISS_QR"
    ),

    [PaymentMethodTypes.BuyClub]: t("payment-method.BUY_CLUB"),
    [PaymentMethodTypes.POS_BIMPLI_CADO]: t("payment-method.POS_BIMPLI_CADO"),
    [PaymentMethodTypes.POS_KADEOS_INFINI]: t(
      "payment-method.POS_KADEOS_INFINI"
    ),
    [PaymentMethodTypes.POS_ANCV_CONNECT]: t("payment-method.POS_ANCV_CONNECT"),
    [PaymentMethodTypes.TOUT_UN_CANTON]: t("payment-method.TOUT_UN_CANTON"),
    [PaymentMethodTypes.CHAPLINS]: t("payment-method.CHAPLINS"),
    [PaymentMethodTypes.HELLO_CSE]: t("payment-method.HELLO_CSE"),
    [PaymentMethodTypes.Z_CH_GUTSCHEIN]: t("payment-method.Z_CH_GUTSCHEIN"),
  } as {
    [key in PaymentMethodTypes]: string;
  };
});

/**
 * Selected config getter
 */
export const selectedConfig = computed(() => {
  return POSConfigModule.selectedConfig;
});

/**
 * do not set anything to the state
 * fetch product categories of the selected config from remote db and store them locally
 *
 */
export async function fetchConfigPCs(config: IPOSConfig, signal?: AbortSignal) {
  const ticketGroups = config.ticketGroups || "";
  if (!ticketGroups) {
    throw new Error(POSConfigErrors.NoTicketGroups);
  }

  LogService.info(
    `Going to fetch and store product categories of the selected config: `,
    {
      config,
    }
  );

  // Fetch all PCs from remote, then atomically swap into Dexie
  await fetchAllTicketGroupsPCs(ticketGroups, signal);

  // After PCs are loaded, check that all of their addons have been loaded as well
  if (!signal?.aborted) {
    await checkAndFetchPCAddons();
  }
}

/**
 * do not set anything to the state
 * fetch timeslots of the selected config from remote db and store them locally
 */
export async function fetchConfigTimeslots(
  config: IPOSConfig,
  signal?: AbortSignal
) {
  CommonModule.clearTicketGroupLoadedDates();

  // Stream all PCs from Dexie, collecting only the IDs we need
  const categoryIdsSet = new Set<number>();
  const retailType = PCTypes[PCTypes.Retail];
  const seatedSubType = PCTypes[PCTypes.SeatedSub];

  await dexieDB.productCategories.each((pc) => {
    if (pc.type === retailType) {
      return;
    }
    // For SeatedSub PCs, use the parent category ID for timeslot fetching
    if (pc.type === seatedSubType && pc.seatingParentPSCId) {
      categoryIdsSet.add(pc.seatingParentPSCId);
    } else {
      categoryIdsSet.add(pc.id);
    }
  });

  if (categoryIdsSet.size) {
    await fetchAllPCsTimeslots([...categoryIdsSet], signal);
  }

  LogService.info(
    `Done fetching and storing product categories of the selected config: `,
    {
      config,
    }
  );

  // seed each ticket group with today as a loaded date.
  // Local midnight, not UTC: a UTC-midnight date lands on the previous day for users
  // behind UTC and is dropped by CalendarCards' local-day filter, emptying the picker.
  const d = new Date();
  POSConfigModule.sortedGrids.forEach((grid) => {
    CommonModule.addTicketGroupLoadedDates({
      ticketGroupId: grid.ticketGroupId,
      date: new Date(d.getFullYear(), d.getMonth(), d.getDate()),
    });
  });

  // Now lets clear the unfinished orders from the state
  clearUnfinishedOrders();
}

export function getDefaultPaymentMethod() {
  const usedPaymentMethod = POSConfigModule.getPaymentSettings.filter(
    (paymentMethod: any) => {
      return paymentMethod.used === true;
    }
  )[0];

  return usedPaymentMethod;
}

export async function fetchConfigPicture(configId: number) {
  try {
    const pictures = await getSmeetzPictures(
      SmeetzPictureEntityTypes.POSConfig,
      [configId]
    );
    if (pictures.length > 0) {
      const configPicture = pictures[0];
      const configPictureUrl =
        createCroppedImageproxyUrl({
          name: configPicture?.name,
          table: SmeetzPictureEntityTables.POSConfig,
          metaData: configPicture?.metaData,
        }) || "";
      POSConfigModule.setConfigPictureUrl(configPictureUrl);
    }
  } catch (error) {
    LogService.info(
      `Error when fetching POSConfig picture, configId: ${configId}`,
      { error },
      LogLevel.Error
    );
  }
}
